
import { defineComponent, onMounted, getCurrentInstance, computed, onActivated, onUnmounted, ref, PropType, watch, nextTick } from 'vue';
import cookies from 'js-cookie';
import { AIChat, AIMessage, AIMessageStatus, AIMessageType } from '~/store/modules/ai-conversation';
import { AIFeed } from '~/api/ai-feed';
import { generateUUID, getQuery } from '~/common/utils';
import { AIChatHandler } from '@/pages/aichat/scripts/ai-chat-handler';
import AIChatMessageIntro from './messages-lists/message-intro.vue';
import AIChatMessageText from './messages-lists/message-text.vue';
import { UserInfoByLogin } from '~/api/user';
import { AIFeedPageEnum } from '~/store/modules/ai-feed';
import { MessageAudioAutoPlayerControllerInstance, AudioAutoPlayMessage } from '@/pages/aichat/scripts/message-audio-auto-player';

enum AIChatMode {
    history,
    chat,
}

const AIChatComponent = defineComponent({
    components: { MessageIntroduction: AIChatMessageIntro, MessageText: AIChatMessageText },
    props: {
        aiFeed: {
            type: Object as PropType<AIFeed> | undefined,
            default: undefined,
        },
        currentFeed: {
            type: Object as PropType<AIFeed> | undefined,
            default: undefined,
        },
        index: {
            type: Number,
            default: 0,
        },
        curIndex: {
            type: Number,
            default: 0,
        },
        feedPage: {
            type: String as PropType<AIFeedPageEnum> | undefined,
            default: AIFeedPageEnum.AIForyou,
        },
        isShowLeadChat: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const { proxy } = getCurrentInstance() as { proxy: any };
        const showIntroduction = ref(true);
        const lastMessage = computed<AIMessage>(() => {
            const { conversations } = proxy?.$store?.state?.conversationModule;
            const chat: AIChat = conversations?.[props.aiFeed?.bot_id_str || ''];
            const messages = chat?.messages;
            return messages?.[messages?.length - 1];
        });

        const messageList = computed<AIMessage[]>(() => {
            const { conversations } = proxy?.$store?.state?.conversationModule;
            const chat: AIChat = conversations?.[props.aiFeed?.bot_id_str || ''];
            const messages = chat?.messages;
            const count = messages?.length;
            const filteredMessages = messages?.filter((message, index) => {
                return (index !== count - 1 && message.messageStatus !== AIMessageStatus.loading) || index === count - 1;
            });
            return filteredMessages;
        });

        const messageCount = computed(() => {
            return messageList.value?.length;
        });
        const historyMode = ref(AIChatMode.history);
        const isHistoryMode = computed(() => {
            return historyMode.value === AIChatMode.history;
        });
        const curBotIndexNumber = computed(() => {
            return props.curIndex;
        });
        const beginSlideIn = ref(false);
        const setInitialPositionDown = computed(() => {
            return props?.index - props?.curIndex === 1 && messageCount.value < 3;
        });

        const setInitialPositionUp = computed(() => {
            return props?.index - props?.curIndex === -1 && messageCount.value < 3;
        });

        const onHandleClickDescription = () => {
            proxy?.$store?.commit?.('popupModule/setShowAIInfo', true);
        };

        const userInfoByLogin = computed<UserInfoByLogin>(() => {
            const userInfoByLogin = (proxy?.$store?.state?.userModule?.userInfo || {}) as UserInfoByLogin;
            return userInfoByLogin;
        });

        const userId = computed(() => {
            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || cookies.get('userId');
            return uid; // '150001356314096';
        });
        const scrollToBottom = () => {
            // eslint-disable-next-line no-use-before-define
            const scrollHeight = historyContainerRef?.value?.scrollHeight;
            if (scrollHeight > 0) {
                // eslint-disable-next-line no-use-before-define
                historyContainerRef.value.scrollTo(0, scrollHeight);
            }
        };

        const heightStyle = computed(() => {
            return {
                maxHeight: props.isShowLeadChat ? 'calc(var(--view-port-height) - 404px)' : 'calc(var(--view-port-height) - 254px)',
            };
        });

        const dynamicHeight = computed(() => {
            return props.isShowLeadChat ? 404 : 254;
        });

        // Animations
        const introductionRefs = ref();
        const chatContainerRef = ref();
        const historyContainerRef = ref();
        const containerWrapperRef = ref();
        const introductionHeight = ref(0);
        const containerHeight = ref(0);
        const containerStyle = computed(() => {
            const firstShowHeight = containerHeight.value - introductionHeight.value - 32;
            return {
                '--introduction-ratio': `${firstShowHeight}px`,
                '--container-ratio': `${containerHeight.value}px`,
                '--view-port-height': `${window?.innerHeight}px`,
                '--dynamic-height': `${dynamicHeight.value}px`,
            };
        });

        // Events
        const isExpanded = ref(false);
        const isInitialAtTop = ref(false);
        const isInitialAtBottom = ref(false);
        const touchStartY = ref(0);
        const needMask = ref(true);
        const updateNeedMask = () => {
            const hisStorycontainer = historyContainerRef.value;
            const containerWrapper = containerWrapperRef.value;

            if (!hisStorycontainer || !containerWrapper) return;
            needMask.value = hisStorycontainer.scrollHeight > containerWrapper.clientHeight;
        };
        const toggleExpand = () => {
            isExpanded.value = !isExpanded.value;
        };

        const handleChatOtherTouchStart = (e: TouchEvent) => {
            const container = historyContainerRef.value;
            if (!container) return;

            touchStartY.value = e.touches[0].clientY;

            // 记录初始位置状态
            isInitialAtTop.value = container.scrollTop <= 0;
            isInitialAtBottom.value = Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) <= 1;
        };

        const handleChatOtherTouchMove = (e: TouchEvent) => {
            if (!isExpanded.value) return;

            const currentY = e.touches[0].clientY;
            const isScrollingUp = currentY < touchStartY.value;

            // 展开状态下：
            // 1. 在顶部向下滑动不阻止
            // 2. 在底部向上滑动不阻止
            // 3. 其他情况阻止
            if (!(isInitialAtTop.value && !isScrollingUp) && !(isInitialAtBottom.value && isScrollingUp)) {
                e.stopPropagation();
            }
        };

        const handleChatOtherTouchEnd = (e: TouchEvent) => {};

        // 折叠态、非折叠态切换动画
        const isAnimating = ref(false);
        const animationID = ref(0);
        const monitorTransition = () => {
            const container = historyContainerRef.value;
            if (!container || !isAnimating.value) return;
            // scrollToBottom();
            animationID.value = requestAnimationFrame(monitorTransition);
        };
        const onTransitionStart = () => {
            isAnimating.value = true;
            monitorTransition();
            // needMask.value = false;
        };

        const onTransitionEnd = () => {
            isAnimating.value = false;
            cancelAnimationFrame(animationID.value);
            animationID.value = 0;
            updateNeedMask();
        };

        const isMockMessageStored = computed(() => {
            return proxy?.$store?.state?.conversationModule?.isMockMessageInserted;
        });

        watch(
            () => lastMessage.value,
            (newVal, oldVal) => {
                if (newVal && newVal.messageType === AIMessageType.me) {
                    const identifier = AIChatHandler?.generateLuigiId(props.aiFeed?.bot_id_str, props?.aiFeed?.conversation_id, oldVal?.messageId);
                    AIChatHandler?.disposeChat(identifier);
                    showIntroduction.value = false;
                }
            }
        );

        watch(
            [() => lastMessage?.value?.content, () => messageList.value],
            async ([newContent, newList], [oldContent, oldList]) => {
                if (props.feedPage === AIFeedPageEnum.AIForyou && (!oldContent || !oldList || newContent === oldContent || newList === oldList)) {
                    return;
                }

                await nextTick();
                scrollToBottom();
                updateNeedMask();
            },
            { immediate: true } // 确保在初始渲染时也执行监听器
        );

        watch(
            () => props?.currentFeed,
            newVal => {
                beginSlideIn.value = props?.aiFeed?.bot_id_str === newVal?.bot_id_str && messageCount.value < 3;
            }
        );

        watch(
            () => historyMode.value,
            async (newVal, oldVal) => {
                if (newVal !== oldVal && newVal === AIChatMode.history) {
                    await nextTick();
                    scrollToBottom();
                    beginSlideIn.value = false;
                }
            }
        );

        watch([() => setInitialPositionDown.value, () => setInitialPositionUp.value], ([newDown, newUp], [oldDown, oldUp]) => {
            proxy?.$nextTick(() => {
                const introductionRef = introductionRefs.value?.[0];
                introductionHeight.value = introductionRef?.clientHeight || 0;
                containerHeight.value = historyContainerRef.value?.clientHeight || 0;
            });
        });

        watch(
            () => props?.currentFeed,
            async newVal => {
                const { automaticplay } = proxy?.$store?.state?.abTestModule?.abTestParams || {};
                if (!automaticplay || proxy?.$isAppEnvironment) {
                    MessageAudioAutoPlayerControllerInstance.onDestroy();
                    return;
                }
                if (newVal?.bot_id_str === props?.aiFeed?.bot_id_str) {
                    const messageListWithAudio: AudioAutoPlayMessage[] = messageList.value
                        ?.filter(item => {
                            return (
                                item?.messageStatus === AIMessageStatus.done &&
                                props?.aiFeed?.showTone &&
                                item.messageType !== AIMessageType.me &&
                                item.messageType !== AIMessageType.introducation &&
                                !!item.content
                            );
                        })
                        .map(item => ({
                            messageId: item?.messageId,
                            botId: item?.botId,
                            botName: props?.aiFeed?.bot_name || '',
                            messageTextContent: item?.content || '',
                            isIntro: item.messageType === AIMessageType.hi,
                        }));

                    MessageAudioAutoPlayerControllerInstance.onAudioMessageListChange(proxy?.$isAppEnvironment ? messageListWithAudio || [] : []);
                    await MessageAudioAutoPlayerControllerInstance.emit({
                        onPlayLoadingCallback: () => {
                            console.log('onPlayLoadingCallback');
                        },
                        onPLayCallback: () => {
                            console.log('onPLayCallback');
                        },
                        onPlayEndCallback: () => {
                            console.log('onPlayEndCallback');
                        },
                    });
                }
            },
            { immediate: true }
        );

        onMounted(() => {
            console.log('props.index', props.index);
            const messageId = generateUUID();
            proxy?.$store?.commit('conversationModule/storeMessage', {
                botId: props?.aiFeed?.bot_id_str,
                message: `${props?.aiFeed?.bot_text}`,
                messageId,
                type: AIMessageType.hi,
            });

            const introMessageId = generateUUID();
            proxy?.$store?.commit('conversationModule/storeMessage', {
                botId: props?.aiFeed?.bot_id_str,
                message: `${props?.aiFeed?.bot_introduction}`,
                messageId: introMessageId,
                type: AIMessageType.introducation,
            });

            // push/message来源mock message
            const { push_description } = proxy?.$route?.query || '';
            const { botId } = proxy?.$route?.params || {};
            const aiBotId = props?.aiFeed?.bot_id_str;
            if (
                !isMockMessageStored.value &&
                push_description &&
                aiBotId === botId &&
                props.index === 0 &&
                props.feedPage === AIFeedPageEnum.AIForyou
            ) {
                proxy?.$store?.commit('conversationModule/storeMessage', {
                    botId: props?.aiFeed?.bot_id_str,
                    message: push_description,
                    messageId: generateUUID(),
                    type: AIMessageType.hi_mock_message,
                });
                proxy?.$store?.commit?.('conversationModule/insertMockMessage');
            }

            proxy?.$bus.on('switchChatMode', (aiFeed: AIFeed, history: Boolean) => {
                if (aiFeed !== props.aiFeed) return;
                historyMode.value = history ? AIChatMode.history : AIChatMode.chat;
            });
            proxy?.$bus.emit('switchChatMode', props.aiFeed, true);
            updateNeedMask();
        });

        onUnmounted(() => {});

        onActivated(() => {});
        return {
            showIntroduction,
            onHandleClickDescription,
            beginSlideIn,
            containerStyle,
            setInitialPositionDown,
            setInitialPositionUp,
            chatContainerRef,
            introductionRefs,
            historyContainerRef,
            lastMessage,
            messageList,
            historyMode,
            isHistoryMode,
            AIChatMode,
            AIMessageType,
            isExpanded,
            handleChatOtherTouchStart,
            handleChatOtherTouchMove,
            handleChatOtherTouchEnd,
            onTransitionStart,
            onTransitionEnd,
            toggleExpand,
            needMask,
            curBotIndexNumber,
            heightStyle,
            dynamicHeight,
        };
    },
});
export default AIChatComponent;
